// javascript
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify'; // <-- 1. IMPORTAR TOAST
import { cambioEstadoService } from '../services/cambioEstadoService';
import { useAuth } from '../auth/AuthContext'; // Importar AuthContext
import { AMBITOS_FLUJO_SIMPLE, transicionesPermitidas } from '../utils/flujoEstados';
import '../style/components/ModalHistorialEstados.css';

// Roles habilitados a cambiar estado en los módulos previsionales simples. Coincide con lo
// que el backend ya permite (SecurityConfig sobre POST /tramites/*/estados + PermisoTramiteService),
// que es más amplio que el gate histórico de este modal (solo ADMIN).
const ROLES_CAMBIO_ESTADO_SIMPLE = ['ADMIN', 'PROGRAMADOR', 'RECEPCION'];

const ModalHistorialEstados = ({ isOpen, onClose, idTramite, onEstadoCambiado, ambitoFiltro }) => {
    const { user } = useAuth(); // Obtener usuario del contexto
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [mostrarFormCambio, setMostrarFormCambio] = useState(false);
    const [nuevoEstado, setNuevoEstado] = useState({
        idEstado: '',
        comentario: ''
    });
    const [cambiandoEstado, setCambiandoEstado] = useState(false);
    const [estadosDisponibles, setEstadosDisponibles] = useState([]);
    const [cargandoEstados, setCargandoEstados] = useState(false);
    // Transición guiada elegida (solo módulos simples): guarda el destino que el usuario
    // clickeó mientras completa el comentario.
    const [transicionElegida, setTransicionElegida] = useState(null);
    const [comentarioTransicion, setComentarioTransicion] = useState('');
    // Escape hatch de los módulos simples: saltar a cualquier estado del ámbito, para corregir
    // un error de carga o adelantar un paso que no se registró en su momento.
    const [mostrarCambioManual, setMostrarCambioManual] = useState(false);
    // Edición de la fecha de ingreso (inicio del primer estado). null = no se está editando.
    const [fechaIngresoEditada, setFechaIngresoEditada] = useState(null);
    const [guardandoIngreso, setGuardandoIngreso] = useState(false);

    // Qué mecanismo de cambio de estado ofrece el modal según el ámbito:
    //  - RTI/ART: dropdown libre gateado a ADMIN (comportamiento histórico, no se toca).
    //  - Módulos simples: transiciones guiadas por botón.
    //  - REAJUSTE: ninguno; su cambio de estado vive en SeccionAvanzarEstado, en la propia ficha.
    const esFlujoSimple = AMBITOS_FLUJO_SIMPLE.includes(ambitoFiltro);
    const esRtiOArt = ambitoFiltro === 'RTI' || ambitoFiltro === 'ART';

    // Cargar historial cuando se abre el modal o cambia el idTramite
    useEffect(() => {
        if (isOpen && idTramite) {
            console.log("🔄 Modal abierto con ID:", idTramite);
            // Reajuste no ofrece cambio de estado acá: pedir el catálogo sería una request al pedo.
            if (esRtiOArt || esFlujoSimple) {
                cargarEstadosDisponibles();
            }
            cargarHistorialEstados(idTramite);
        } else if (isOpen && !idTramite) {
            console.error("❌ Modal abierto pero idTramite es:", idTramite);
            setError("No se pudo identificar el trámite. ID no válido.");
        }
    }, [isOpen, idTramite]);

    // Cerrar modal con ESC
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);


    // Cargar estados disponibles desde la API
    const cargarEstadosDisponibles = async () => {
        try {
            setCargandoEstados(true);
            const estados = await cambioEstadoService.getEstadosDisponibles();
            // Si se especifica un ámbito (ej. "ART"), filtrar solo esos estados
            const estadosFiltrados = ambitoFiltro
                ? estados.filter((e) => e.ambito === ambitoFiltro)
                : estados;
            setEstadosDisponibles(estadosFiltrados);
            console.log('✅ Estados disponibles cargados:', estadosFiltrados);
        } catch (err) {
            console.error('❌ Error cargando estados:', err);
        } finally {
            setCargandoEstados(false);
        }
    };

    // Función para cargar el historial
    const cargarHistorialEstados = async (tramiteId) => {
        try {
            setLoading(true);
            setError(null);

            const data = await cambioEstadoService.getHistorialEstados(tramiteId);
            setHistorial(data);

            console.log('✅ Historial cargado:', data);
        } catch (err) {
            console.error('❌ Error cargando historial:', err);
            setError(err.message || "Error al cargar el historial");
        } finally {
            setLoading(false);
        }
    };


    // Aplica el cambio de estado y refresca. Lo comparten el dropdown libre de RTI/ART y los
    // botones de transición guiada de los módulos simples.
    const aplicarCambioEstado = async ({ idEstado, comentario }) => {
        try {
            setCambiandoEstado(true);
            await cambioEstadoService.cambiarEstado(idTramite, {
                idEstado: Number(idEstado),
                comentario: comentario?.trim() ? comentario.trim() : null
            });

            // Recargar historial y resetear ambos formularios
            await cargarHistorialEstados(idTramite);
            setNuevoEstado({ idEstado: '', comentario: '' });
            setMostrarFormCambio(false);
            setTransicionElegida(null);
            setComentarioTransicion('');
            setMostrarCambioManual(false);

            toast.success('Estado cambiado exitosamente'); // <-- 3. REEMPLAZADO ALERT

            // Notificar al componente padre para que actualice el trámite
            if (onEstadoCambiado) {
                onEstadoCambiado();
            }

        } catch (err) {
            toast.error(`Error al cambiar estado: ${err.message}`); // <-- 4. REEMPLAZADO ALERT
        } finally {
            setCambiandoEstado(false);
        }
    };

    // Función para cambiar estado (dropdown libre de RTI/ART)
    const handleCambiarEstado = async (e) => {
        e.preventDefault();

        if (!nuevoEstado.idEstado) {
            toast.warn('Por favor selecciona un estado'); // <-- 2. REEMPLAZADO ALERT
            return;
        }

        await aplicarCambioEstado(nuevoEstado);
    };

    // Formatear fecha
    // El primer estado del trámite es su ingreso. Se busca por fecha y no por posición para no
    // depender del orden en que venga el historial.
    const idxIngreso = historial.reduce((idx, c, i) => (
        idx === -1 || (c.fechaHoraInicio && c.fechaHoraInicio < historial[idx].fechaHoraInicio) ? i : idx
    ), -1);

    const guardarFechaIngreso = async () => {
        if (!fechaIngresoEditada) return;
        try {
            setGuardandoIngreso(true);
            await cambioEstadoService.actualizarFechaIngreso(idTramite, fechaIngresoEditada);
            toast.success('Fecha de ingreso actualizada');
            setFechaIngresoEditada(null);
            await cargarHistorialEstados(idTramite);
            if (onEstadoCambiado) onEstadoCambiado();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGuardandoIngreso(false);
        }
    };

    const formatFecha = (fechaString) => {
        if (!fechaString) return 'Actual';

        const fecha = new Date(fechaString);
        return fecha.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calcular duración
    const calcularDuracion = (inicio, fin) => {
        if (!inicio) return '-';

        const fechaInicio = new Date(inicio);
        const fechaFin = fin ? new Date(fin) : new Date();

        const diffMs = fechaFin - fechaInicio;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffDays > 0) {
            return `${diffDays}d ${diffHours}h`;
        } else if (diffHours > 0) {
            return `${diffHours}h ${diffMinutes}m`;
        } else {
            return `${diffMinutes}m`;
        }
    };

    // Cerrar al hacer clic fuera
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    // El estado actual sale del propio historial ya cargado (el cambio abierto es el que no
    // tiene fecha de fin), así no hace falta que las fichas pasen una prop extra.
    const nombreEstadoActual = historial.find(c => !c.fechaHoraFin)?.nombreEstado;
    const transiciones = esFlujoSimple
        ? transicionesPermitidas(estadosDisponibles, nombreEstadoActual, ambitoFiltro)
        : [];
    const puedeCambiarEstadoSimple = user && ROLES_CAMBIO_ESTADO_SIMPLE.includes(user.rol);

    return (
        <div className="modal-historial-backdrop" onClick={handleBackdropClick}>
            <div className="modal-historial-content">
                {/* Header */}
                <div className="modal-historial-header">
                    <div className="modal-title-section">
                        <h2>Historial de Estados</h2>
                        <p className="modal-subtitle">Trámite #{idTramite}</p>
                    </div>
                    <button
                        className="modal-close-button"
                        onClick={onClose}
                        aria-label="Cerrar modal"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                {/* Body */}
                <div className="modal-historial-body">
                    {/* ── RTI / ART: dropdown libre, gate ADMIN/PROGRAMADOR (comportamiento histórico) ── */}
                    {/* Botón para cambiar estado (SOLO ADMIN/PROGRAMADOR) */}
                    {esRtiOArt && user && (user.rol === 'ADMIN' || user.rol === 'PROGRAMADOR') && (
                        <div className="historial-action-bar">
                            <button
                                className="btn-cambiar-estado"
                                onClick={() => setMostrarFormCambio(!mostrarFormCambio)}
                            >
                                <i className={`bi ${mostrarFormCambio ? 'bi-x-circle' : 'bi-pencil'} me-2`}></i>
                                {mostrarFormCambio ? 'Cancelar' : 'Cambiar Estado'}
                            </button>
                        </div>
                    )}

                    {/* Formulario para cambiar estado */}
                    {esRtiOArt && mostrarFormCambio && (
                        <div className="cambio-estado-form-container">
                            <h4>Cambiar Estado del Trámite</h4>
                            <form onSubmit={handleCambiarEstado}>
                                <div className="form-group">
                                    <label htmlFor="idEstado">Nuevo Estado *</label>
                                    <select
                                        id="idEstado"
                                        value={nuevoEstado.idEstado}
                                        onChange={(e) => setNuevoEstado({
                                            ...nuevoEstado,
                                            idEstado: e.target.value
                                        })}
                                        required
                                        disabled={cambiandoEstado || cargandoEstados}
                                    >
                                        <option value="">{cargandoEstados ? 'Cargando estados...' : 'Seleccionar estado...'}</option>
                                        {estadosDisponibles.map(estado => (
                                            <option key={estado.id} value={estado.id}>
                                                {estado.nombre}
                                            </option>
                                        ))}
                                    </select>
                                    {cargandoEstados && (
                                        <small className="text-muted">Cargando estados disponibles...</small>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="comentario">Comentario</label>
                                    <textarea
                                        id="comentario"
                                        value={nuevoEstado.comentario}
                                        onChange={(e) => setNuevoEstado({
                                            ...nuevoEstado,
                                            comentario: e.target.value
                                        })}
                                        rows="3"
                                        placeholder="Observaciones sobre el cambio de estado..."
                                        disabled={cambiandoEstado}
                                        maxLength="1000"
                                    />
                                    <small className="char-count">
                                        {nuevoEstado.comentario.length}/1000 caracteres
                                    </small>
                                </div>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setMostrarFormCambio(false);
                                            setNuevoEstado({ idEstado: '', comentario: '' });
                                        }}
                                        className="btn-cancelar"
                                        disabled={cambiandoEstado}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={cambiandoEstado || !nuevoEstado.idEstado}
                                        className="btn-confirmar"
                                    >
                                        {cambiandoEstado ? (
                                            <>
                                                <i className="bi bi-hourglass-split me-2"></i>
                                                Procesando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Confirmar Cambio
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* ── Módulos previsionales simples: transiciones guiadas ──────────────
                        No es un dropdown libre: se ofrece solo lo que el flujo permite desde
                        el estado actual, un botón por destino. */}
                    {esFlujoSimple && puedeCambiarEstadoSimple && !loading && !error && (
                        <div className="transicion-guiada-container">
                            {cargandoEstados && (
                                <p className="text-muted mb-0">Cargando transiciones disponibles...</p>
                            )}

                            {!cargandoEstados && transiciones.length === 0 && (
                                <div className="transicion-guiada-etapa-final">
                                    <i className="bi bi-flag-fill me-2"></i>
                                    Este trámite está en una etapa final del ciclo de estados.
                                </div>
                            )}

                            {!cargandoEstados && transiciones.length > 0 && (
                                <>
                                    <h4>{transiciones.every((t) => t.tipo === 'ingreso') ? 'Reactivar el trámite' : 'Avanzar el trámite'}</h4>
                                    <div className="transicion-guiada-botones">
                                        {transiciones.map((t) => (
                                            <button
                                                key={t.idEstado}
                                                type="button"
                                                className={`transicion-guiada-btn transicion-guiada-${t.tipo}`}
                                                disabled={cambiandoEstado}
                                                onClick={() => {
                                                    // Re-clickear el mismo botón cierra el mini-form.
                                                    const mismo = transicionElegida?.idEstado === t.idEstado;
                                                    setTransicionElegida(mismo ? null : t);
                                                    setComentarioTransicion('');
                                                }}
                                            >
                                                <i className={`bi ${t.tipo === 'favorable' ? 'bi-check-circle-fill'
                                                    : t.tipo === 'desfavorable' ? 'bi-x-octagon-fill'
                                                        : t.tipo === 'cancelado' ? 'bi-slash-circle-fill'
                                                            : t.tipo === 'ingreso' ? 'bi-arrow-counterclockwise'
                                                                : 'bi-arrow-right-circle-fill'} me-2`}></i>
                                                {t.nombre}
                                            </button>
                                        ))}
                                    </div>

                                    {transicionElegida && (
                                        <div className="transicion-guiada-form">
                                            <label htmlFor="comentarioTransicion">
                                                Comentario <span className="text-muted">(opcional)</span>
                                            </label>
                                            <textarea
                                                id="comentarioTransicion"
                                                value={comentarioTransicion}
                                                onChange={(e) => setComentarioTransicion(e.target.value)}
                                                rows="3"
                                                placeholder={`Por qué el trámite pasa a "${transicionElegida.nombre}"...`}
                                                disabled={cambiandoEstado}
                                                maxLength="1000"
                                            />
                                            <small className="char-count">
                                                {comentarioTransicion.length}/1000 caracteres
                                            </small>

                                            <div className="form-actions">
                                                <button
                                                    type="button"
                                                    className="btn-cancelar"
                                                    disabled={cambiandoEstado}
                                                    onClick={() => {
                                                        setTransicionElegida(null);
                                                        setComentarioTransicion('');
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-confirmar"
                                                    disabled={cambiandoEstado}
                                                    onClick={() => aplicarCambioEstado({
                                                        idEstado: transicionElegida.idEstado,
                                                        comentario: comentarioTransicion
                                                    })}
                                                >
                                                    {cambiandoEstado ? (
                                                        <>
                                                            <i className="bi bi-hourglass-split me-2"></i>
                                                            Procesando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check-lg me-2"></i>
                                                            Confirmar cambio
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Salida de emergencia: cualquier estado del ámbito, sin respetar
                                el camino normal. Va colapsada para que el flujo guiado siga
                                siendo lo primero que se ve. */}
                            {!cargandoEstados && (
                                <div className="cambio-manual-wrapper">
                                    <button
                                        type="button"
                                        className="cambio-manual-toggle"
                                        onClick={() => {
                                            setMostrarCambioManual(!mostrarCambioManual);
                                            setTransicionElegida(null);
                                            setNuevoEstado({ idEstado: '', comentario: '' });
                                        }}
                                    >
                                        <i className={`bi ${mostrarCambioManual ? 'bi-chevron-up' : 'bi-chevron-down'} me-2`}></i>
                                        Cambiar a otro estado
                                    </button>

                                    {mostrarCambioManual && (
                                        <div className="cambio-manual-form">
                                            <div className="form-group">
                                                <label htmlFor="idEstadoManual">Estado destino *</label>
                                                <select
                                                    id="idEstadoManual"
                                                    value={nuevoEstado.idEstado}
                                                    onChange={(e) => setNuevoEstado({
                                                        ...nuevoEstado,
                                                        idEstado: e.target.value
                                                    })}
                                                    disabled={cambiandoEstado}
                                                >
                                                    <option value="">Seleccionar estado...</option>
                                                    {estadosDisponibles.map(estado => (
                                                        <option key={estado.id} value={estado.id}>
                                                            {estado.nombre}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="comentarioManual">Comentario</label>
                                                <textarea
                                                    id="comentarioManual"
                                                    value={nuevoEstado.comentario}
                                                    onChange={(e) => setNuevoEstado({
                                                        ...nuevoEstado,
                                                        comentario: e.target.value
                                                    })}
                                                    rows="3"
                                                    placeholder="Por qué se fuerza este cambio de estado..."
                                                    disabled={cambiandoEstado}
                                                    maxLength="1000"
                                                />
                                                <small className="char-count">
                                                    {nuevoEstado.comentario.length}/1000 caracteres
                                                </small>
                                            </div>

                                            <div className="form-actions">
                                                <button
                                                    type="button"
                                                    className="btn-cancelar"
                                                    disabled={cambiandoEstado}
                                                    onClick={() => {
                                                        setMostrarCambioManual(false);
                                                        setNuevoEstado({ idEstado: '', comentario: '' });
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-confirmar"
                                                    disabled={cambiandoEstado || !nuevoEstado.idEstado}
                                                    onClick={() => aplicarCambioEstado(nuevoEstado)}
                                                >
                                                    {cambiandoEstado ? (
                                                        <>
                                                            <i className="bi bi-hourglass-split me-2"></i>
                                                            Procesando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check-lg me-2"></i>
                                                            Confirmar cambio
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Estados de carga y error */}
                    {loading && (
                        <div className="historial-loading">
                            <div className="loading-spinner"></div>
                            <p>Cargando historial de estados...</p>
                        </div>
                    )}

                    {error && (
                        <div className="historial-error">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <div>
                                <strong>Error al cargar el historial</strong>
                                <p>{error}</p>
                            </div>
                            <button
                                onClick={() => cargarHistorialEstados(idTramite)}
                                className="btn-reintentar"
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {/* Tabla de historial */}
                    {!loading && !error && historial.length === 0 && (
                        <div className="historial-empty">
                            <i className="bi bi-inbox"></i>
                            <h4>No hay historial de estados</h4>
                            <p>Este trámite no tiene cambios de estado registrados.</p>
                        </div>
                    )}

                    {!loading && !error && historial.length > 0 && (
                        <div className="historial-table-container">
                            <div className="table-responsive">
                                <table className="historial-table">
                                    <thead>
                                    <tr>
                                        <th>Estado</th>
                                        <th>Fecha Inicio</th>
                                        <th>Fecha Fin</th>
                                        <th>Duración</th>
                                        <th>Comentario</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {historial.map((cambio, index) => (
                                        <tr
                                            key={cambio.idCambioEstado || index}
                                            className={!cambio.fechaHoraFin ? 'estado-actual' : ''}
                                        >
                                            <td>
                                                    <span className={`estado-badge ${!cambio.fechaHoraFin ? 'badge-actual' : ''}`}>
                                                        {cambio.nombreEstado || 'N/A'}
                                                        {!cambio.fechaHoraFin && (
                                                            <i className="bi bi-star-fill ms-1"></i>
                                                        )}
                                                    </span>
                                            </td>
                                            <td>
                                                {index === idxIngreso && fechaIngresoEditada !== null ? (
                                                    <div className="d-flex align-items-center gap-1">
                                                        <input
                                                            type="date"
                                                            className="form-control form-control-sm"
                                                            style={{ maxWidth: 150 }}
                                                            value={fechaIngresoEditada}
                                                            max={new Date().toLocaleDateString('sv-SE')}
                                                            onChange={(e) => setFechaIngresoEditada(e.target.value)}
                                                            aria-label="Fecha de ingreso"
                                                        />
                                                        <button type="button" className="btn btn-sm btn-primary" disabled={guardandoIngreso || !fechaIngresoEditada} onClick={guardarFechaIngreso} title="Guardar">
                                                            <i className="bi bi-check-lg"></i>
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={guardandoIngreso} onClick={() => setFechaIngresoEditada(null)} title="Cancelar">
                                                            <i className="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="fecha-hora">
                                                        {formatFecha(cambio.fechaHoraInicio)}
                                                        {/* Trámites viejos cargados después de empezar: al crearlos el
                                                            ingreso queda con la fecha de carga y se corrige acá. */}
                                                        {index === idxIngreso && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                                                                title="Cambiar la fecha de ingreso del trámite"
                                                                aria-label="Cambiar la fecha de ingreso del trámite"
                                                                onClick={() => setFechaIngresoEditada(String(cambio.fechaHoraInicio || '').slice(0, 10))}
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="fecha-hora">
                                                    {formatFecha(cambio.fechaHoraFin)}
                                                </div>
                                            </td>
                                            <td>
                                                    <span className="duracion">
                                                        {calcularDuracion(cambio.fechaHoraInicio, cambio.fechaHoraFin)}
                                                    </span>
                                            </td>
                                            <td className="comentario-cell">
                                                {cambio.comentario ? (
                                                    <div
                                                        className="comentario-text"
                                                        title={cambio.comentario}
                                                    >
                                                        {cambio.comentario}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Resumen */}
                            <div className="historial-summary">
                                <div className="summary-item">
                                    <span className="summary-label">Total de cambios:</span>
                                    <span className="summary-value">{historial.length}</span>
                                </div>
                                <div className="summary-item">
                                    <span className="summary-label">Estado actual:</span>
                                    <span className="summary-value text-success">
                                        {historial.find(c => !c.fechaHoraFin)?.nombreEstado || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-historial-footer">
                    <button
                        onClick={onClose}
                        className="btn-cerrar-modal"
                    >
                        <i className="bi bi-x-lg me-2"></i>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalHistorialEstados;

